import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Calendar } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { useCalendly } from "@/components/CalendlyButton";

const HERO_IMG = "/founder/hero-office.jpeg";
const FOUNDER_IMG = "/founder/founder-family-bw.jpeg";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const openCalendly = useCalendly();

  return (
    <main data-testid="home-page">
      {/* HERO — WHO + WHY */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7 z-10">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.8 }}
              className="text-xs uppercase tracking-[0.3em] text-terracotta mb-8"
            >
              Motherhood Is Leadership™
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="font-serif font-light text-wine text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight"
            >
              You&rsquo;ve built the career.
              <span className="block text-terracotta">Now lead what comes next.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.9 }}
              className="mt-10 max-w-xl text-base md:text-lg text-charcoal/80 leading-relaxed"
            >
              The Thrive &amp; Mother Company™ helps ambitious, high-performing mothers navigate career and
              leadership inflection points, advocate for the value they create, and build sustainable success
              around the outcomes that matter most.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.9 }}
              className="mt-12 flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/lead"
                data-testid="hero-coaching-cta"
                className="group inline-flex items-center justify-center gap-3 rounded-full bg-terracotta text-cream px-8 py-4 text-sm font-medium tracking-wide hover:bg-wine transition-colors duration-300"
              >
                Explore 1:1 Coaching
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/method"
                data-testid="hero-approach-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-gold text-wine px-8 py-4 text-sm font-medium tracking-wide hover:bg-gold/10 transition-colors duration-300"
              >
                Explore the T&amp;M Approach
              </Link>
            </motion.div>
          </div>

          <div className="lg:col-span-5 z-10">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
              className="relative aspect-[3/4] overflow-hidden"
            >
              <motion.img
                src={HERO_IMG}
                alt="A working mother at her desk"
                style={{ y: imgY, scale: imgScale }}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/25 to-transparent" />
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* OUTCOME-LED PROFESSIONAL — BRIEF INTRO */}
      <section className="relative py-24 md:py-32 bg-wine text-cream overflow-hidden" data-testid="outcome-led-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.3em] text-blush mb-6">Our Philosophy</p>
              <h2 className="font-serif font-light text-cream text-4xl md:text-5xl leading-[1.15]">
                Outcomes over hours.
              </h2>
              <p className="mt-6 text-blush italic font-serif text-lg">
                A better model of success.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7 space-y-8 text-lg text-cream/85 leading-relaxed">
            <Reveal>
              <p className="font-serif font-light text-2xl md:text-3xl text-cream leading-snug">
                The Outcome-led Professional™ — someone who delivers exceptional value through outcomes,
                trust, and impact rather than by visibility or hours worked.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <Link
                to="/method"
                data-testid="outcome-led-cta"
                className="group inline-flex items-center gap-3 rounded-full bg-cream text-wine px-8 py-4 text-sm font-medium tracking-wide hover:bg-blush transition-colors duration-300"
              >
                Explore the T&amp;M Approach
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MEET JENNA — SHORT */}
      <section className="py-24 md:py-32 bg-blush/30" data-testid="founder-teaser">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="relative aspect-[3/4] overflow-hidden max-w-md">
                <img
                  src={FOUNDER_IMG}
                  alt="Jenna Martins with her son"
                  className="w-full h-full object-cover object-[50%_35%]"
                />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-6">Meet Jenna</p>
              <blockquote className="font-serif font-light text-wine text-3xl md:text-4xl lg:text-5xl leading-[1.25]">
                &ldquo;I spent my career learning how to lead organizations. Motherhood taught me how to lead
                with greater purpose. The Thrive &amp; Mother Company™ exists to help women do both.&rdquo;
              </blockquote>
              <p className="mt-8 text-charcoal/80">
                <span className="font-medium text-wine">Jenna Martins</span> — Founder &amp; Chief Visionary
                Officer
              </p>
              <Link
                to="/about"
                data-testid="founder-read-more"
                className="link-underline inline-flex items-center gap-2 mt-8 text-terracotta font-medium"
              >
                Read her story <ArrowRight size={16} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* THE ECOSYSTEM — TWO PATHS */}
      <section className="py-24 md:py-32" data-testid="ecosystem-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-7">
              <Reveal>
                <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-6">Ways To Work Together</p>
                <h2 className="font-serif font-light text-wine text-4xl md:text-5xl leading-tight">
                  Two ways to put the approach into practice.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.1}>
                <p className="text-charcoal/80 leading-relaxed">
                  Whether you&rsquo;re navigating a meaningful career or leadership inflection point, or
                  looking for practical tools to lead your week with more intention — T&amp;M meets you
                  where you are.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gold/30 border border-gold/30">
            {/* Path 1 — Coaching */}
            <Reveal>
              <div className="bg-cream p-10 md:p-14 h-full flex flex-col" data-testid="path-coaching-card">
                <span className="text-xs uppercase tracking-[0.25em] text-gold">01</span>
                <p className="mt-6 text-xs uppercase tracking-[0.3em] text-terracotta">
                  Private Leadership &amp; Career Strategy
                </p>
                <h3 className="mt-4 font-serif text-2xl md:text-3xl text-wine leading-snug">
                  Get intentional about what comes next.
                </h3>
                <p className="mt-4 text-charcoal/75 leading-relaxed flex-1">
                  Work 1:1 with Jenna to clarify what you want next, strengthen how you lead and advocate for
                  the value you create, and build an outcome-led strategy for moving forward.
                </p>
                <div className="mt-8 pt-6 border-t border-gold/30">
                  <Link
                    to="/lead"
                    data-testid="coaching-explore-cta"
                    className="group inline-flex items-center gap-3 rounded-full bg-terracotta text-cream px-8 py-4 text-sm font-medium tracking-wide hover:bg-wine transition-colors duration-300"
                  >
                    Explore 1:1 Coaching
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            </Reveal>

            {/* Path 2 — Resources */}
            <Reveal delay={0.1}>
              <div className="bg-cream p-10 md:p-14 h-full flex flex-col" data-testid="path-resources-card">
                <span className="text-xs uppercase tracking-[0.25em] text-gold">02</span>
                <p className="mt-6 text-xs uppercase tracking-[0.3em] text-terracotta">Practical Resources</p>
                <h3 className="mt-4 font-serif text-2xl md:text-3xl text-wine leading-snug">
                  Start with your week.
                </h3>
                <p className="mt-4 text-charcoal/75 leading-relaxed flex-1">
                  The Working Mom Weekly Reset™ turns the RESET Method™ into a guided, repeatable practice
                  for creating greater clarity and intentionality in everyday life.
                </p>
                <div className="mt-8 pt-6 border-t border-gold/30 flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/shop"
                    data-testid="resources-explore-cta"
                    className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-terracotta text-terracotta px-6 py-3 text-sm font-medium tracking-wide hover:bg-terracotta hover:text-cream transition-colors duration-300"
                  >
                    Explore Resources
                  </Link>
                  <Link
                    to="/free-preview"
                    data-testid="resources-preview-cta"
                    className="inline-flex items-center gap-2 text-terracotta font-medium hover:text-wine transition-colors px-2 py-3 text-sm"
                  >
                    Try a free preview
                    <ArrowUpRight size={16} />
                  </Link>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* DISCOVERY CALL BANNER */}
      <section className="bg-wine text-cream py-14 md:py-16" data-testid="discovery-banner">
        <div className="max-w-6xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-8">
          <Reveal>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-blush mb-3">
                Private Leadership &amp; Career Strategy
              </p>
              <h2 className="font-serif font-light text-2xl md:text-3xl leading-tight">
                Ready to get intentional about what comes next?
              </h2>
              <p className="mt-2 text-cream/75 text-sm md:text-base max-w-lg">
                A confidential 30-minute conversation with Jenna to explore where you are, what you&rsquo;re
                navigating, and whether working together may be a fit.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <button
              onClick={() => openCalendly({ medium: "banner", content: "footer-discovery" })}
              data-testid="home-banner-book-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-cream text-wine px-8 py-4 text-sm font-medium tracking-wide hover:bg-blush transition-colors duration-300 whitespace-nowrap"
            >
              <Calendar size={16} />
              Book a Discovery Call
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
